import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { fmtNum } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

export default function EnquiryPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rate, setRate] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    company_name: "", contact_person: user?.name || "", email: user?.email || "", phone: "",
    shipment_type: "export", pol: "", pod: "", shipping_line: "",
    container_type: "dry", container_size: "", num_containers: Number(params.get("qty")) || 1,
    cargo_description: "", cargo_weight: "", cargo_volume: "", hazardous: false,
    preferred_etd: "", additional_requirements: "",
  });

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, contact_person: f.contact_person || user.name, email: f.email || user.email }));
  }, [user]);

  useEffect(() => {
    const rid = params.get("rate_id");
    if (rid) {
      api.get(`/rates/${rid}`).then(({ data }) => {
        setRate(data);
        setForm((f) => ({
          ...f, shipment_type: data.shipment_type, pol: data.pol_name, pod: data.pod_name,
          shipping_line: data.shipping_line_name, container_type: data.container_type,
          container_size: data.size_label,
        }));
      });
    }
  }, [params]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.company_name || !form.contact_person || !form.email || !form.phone) {
      toast.error("Please fill company, contact, email and phone.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, selected_rate: rate ? {
        shipping_line: rate.shipping_line_name, pol: rate.pol_name, pod: rate.pod_name,
        ocean_freight: rate.ocean_freight, currency: rate.currency,
        size_label: rate.size_label, valid_until: (rate.valid_until || "").slice(0, 10),
      } : null };
      await api.post("/enquiries", payload);
      setSubmitted(true);
      toast.success("Enquiry submitted — our team will confirm shortly.");
    } catch {
      toast.error("Failed to submit enquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="h-16 w-16 bg-signal/10 border border-signal/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-8 w-8 text-signal" />
          </div>
          <h1 className="font-heading font-extrabold text-3xl text-navy">Enquiry Submitted</h1>
          <p className="text-muted-foreground mt-3">Thank you. The VK Forwarders pricing team has received your shipment enquiry and will confirm final rates shortly.</p>
          <div className="flex gap-3 justify-center mt-8">
            <Button className="bg-signal text-white rounded-sm" onClick={() => navigate("/dashboard")}>View My Enquiries</Button>
            <Button variant="outline" className="rounded-sm" onClick={() => navigate("/rates?shipment_type=export")}>Search More Rates</Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-navy text-white py-10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl">Plan Your Shipment</h1>
          <p className="text-white/60 mt-2">Tell us your requirements and we'll confirm your final booking rate.</p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 border border-border rounded-md p-6 bg-card space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Company Name *"><Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className="rounded-sm" data-testid="enq-company" /></Field>
            <Field label="Contact Person *"><Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} className="rounded-sm" data-testid="enq-contact" /></Field>
            <Field label="Email *"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-sm" data-testid="enq-email" /></Field>
            <Field label="Phone *"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-sm" data-testid="enq-phone" /></Field>
          </div>

          <div className="border-t border-border pt-5 grid sm:grid-cols-2 gap-4">
            <Field label="Shipment Type">
              <Select value={form.shipment_type} onValueChange={(v) => set("shipment_type", v)}>
                <SelectTrigger className="rounded-sm" data-testid="enq-stype"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="export">Export</SelectItem><SelectItem value="import">Import</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Shipping Line"><Input value={form.shipping_line} onChange={(e) => set("shipping_line", e.target.value)} className="rounded-sm" data-testid="enq-line" /></Field>
            <Field label="POL"><Input value={form.pol} onChange={(e) => set("pol", e.target.value)} className="rounded-sm" data-testid="enq-pol" /></Field>
            <Field label="POD"><Input value={form.pod} onChange={(e) => set("pod", e.target.value)} className="rounded-sm" data-testid="enq-pod" /></Field>
            <Field label="Container Size"><Input value={form.container_size} onChange={(e) => set("container_size", e.target.value)} className="rounded-sm" data-testid="enq-size" /></Field>
            <Field label="Number of Containers"><Input type="number" min="1" value={form.num_containers} onChange={(e) => set("num_containers", Number(e.target.value))} className="rounded-sm font-mono-data" data-testid="enq-qty" /></Field>
            <Field label="Cargo Weight"><Input value={form.cargo_weight} onChange={(e) => set("cargo_weight", e.target.value)} placeholder="e.g. 18 MT" className="rounded-sm" data-testid="enq-weight" /></Field>
            <Field label="Cargo Volume"><Input value={form.cargo_volume} onChange={(e) => set("cargo_volume", e.target.value)} placeholder="e.g. 28 CBM" className="rounded-sm" data-testid="enq-volume" /></Field>
            <Field label="Preferred ETD"><Input type="date" value={form.preferred_etd} onChange={(e) => set("preferred_etd", e.target.value)} className="rounded-sm font-mono-data" data-testid="enq-etd" /></Field>
          </div>

          <Field label="Cargo Description"><Textarea value={form.cargo_description} onChange={(e) => set("cargo_description", e.target.value)} className="rounded-sm" data-testid="enq-cargo" /></Field>
          <Field label="Additional Requirements"><Textarea value={form.additional_requirements} onChange={(e) => set("additional_requirements", e.target.value)} className="rounded-sm" data-testid="enq-additional" /></Field>

          <div className="flex items-center gap-2">
            <Checkbox id="haz" checked={form.hazardous} onCheckedChange={(v) => set("hazardous", !!v)} data-testid="enq-haz" />
            <Label htmlFor="haz" className="text-sm">Hazardous cargo</Label>
          </div>

          <Button className="w-full bg-signal hover:bg-signal/90 text-white rounded-sm h-11" onClick={submit} disabled={loading} data-testid="enq-submit">
            <Send className="h-4 w-4 mr-2" /> {loading ? "Submitting…" : "Submit Shipment Enquiry"}
          </Button>
        </div>

        <div className="lg:col-span-1">
          {rate ? (
            <div className="border border-border rounded-md bg-card overflow-hidden sticky top-20">
              <div className="bg-navy text-white p-5"><div className="text-xs uppercase text-white/50">Selected Rate</div><div className="font-heading font-bold text-lg mt-1">{rate.shipping_line_name}</div></div>
              <div className="p-5 space-y-2.5 text-sm">
                <Row k="Route" v={`${rate.pol_name} → ${rate.pod_name}`} />
                <Row k="Container" v={rate.size_label} />
                <Row k="Ocean Freight" v={`${rate.currency} ${fmtNum(rate.ocean_freight)}`} />
                <Row k="Local Charges" v={`INR ${fmtNum(rate.local_charges_inr)}`} />
                <Row k="Transit" v={`${rate.transit_time} days`} />
                <Row k="Valid Until" v={(rate.valid_until || "").slice(0, 10)} />
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-border rounded-md p-6 text-sm text-muted-foreground">
              No rate selected. You can still submit a general enquiry, or <button className="text-signal underline" onClick={() => navigate("/rates?shipment_type=export")}>browse rates</button> first.
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

const Field = ({ label, children }) => (
  <div><Label className="text-xs mb-1.5 block text-muted-foreground">{label}</Label>{children}</div>
);
const Row = ({ k, v }) => (
  <div className="flex justify-between border-b border-border pb-2 last:border-0"><span className="text-muted-foreground">{k}</span><span className="font-mono-data text-navy text-right">{v}</span></div>
);
